import { useMemo, useState } from "react";
import { Check, Copy, MailPlus, Share2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { apiRequest } from "@/lib/api";
import { uniqueGuestCount } from "@/lib/holidayPilot";
import { trackReunionEvent } from "@/lib/analytics";
import { fragmentInvitationUrl } from "@/lib/invitationTransport";
import { toast } from "@/components/ui/sonner";

const initialInviteForm = { member_ids: [], guest_emails: "", note: "" };

const trackFirstInvitationShared = () => {
  const key = "kindred:first-invitation-shared";
  if (window.sessionStorage.getItem(key)) return;
  window.sessionStorage.setItem(key, "shared");
  trackReunionEvent("first_invitation_shared", { source: "gathering_invites" });
};

const ShareMessageButton = ({ isFirst, isReunion, onSignal, text, testId }) => {
  const [copied, setCopied] = useState(false);
  const handleShare = async () => {
    if (isReunion) {
      trackReunionEvent("invitation_share_initiated", {
        source: "gathering_invites",
        status: navigator.share ? "native_share" : "copy",
      });
      if (isFirst) trackFirstInvitationShared();
    }
    if (navigator.share) {
      try {
        await navigator.share({ text });
        onSignal?.("shared");
        return;
      } catch { /* cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    onSignal?.("shared");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      className="mt-1 shrink-0 rounded-full border border-border p-1.5 text-muted-foreground hover:bg-muted/60 hover:text-primary transition"
      data-testid={testId}
      onClick={handleShare}
      title="Share invite message"
      type="button"
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Share2 className="h-3.5 w-3.5" />}
    </button>
  );
};

const CopyRsvpLinkButton = ({ inviteId, isFirst, isReunion, onSignal }) => {
  const [copied, setCopied] = useState(false);
  const link = fragmentInvitationUrl(
    typeof window !== "undefined" ? window.location.origin : "https://heykindred.org",
    inviteId
  );
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = link;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    if (isReunion) {
      trackReunionEvent("invite_link_copied", { source: "gathering_invites" });
      if (isFirst) trackFirstInvitationShared();
    }
    onSignal?.("link_copied");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-primary hover:bg-muted/60 transition"
      data-testid={`gatherings-event-invite-rsvp-link-${inviteId}`}
      onClick={handleCopy}
      title="Copy a no-app RSVP link to send this person — they can reply without installing Kindred"
      type="button"
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? "Copied!" : "Copy RSVP link"}
    </button>
  );
};

export const GatheringInvites = ({ event, token, members, onUpdate }) => {
  const [form, setForm] = useState(initialInviteForm);
  const [draftPreviewed, setDraftPreviewed] = useState(false);
  const isOrganizerDraft = event.publication_state === "organizer_draft";
  const guestCount = useMemo(
    () => uniqueGuestCount(form.guest_emails),
    [form.guest_emails]
  );

  // Record a content-free organizer activation signal so the pilot state
  // machine reflects that a link actually left the app. Best-effort: a failure
  // here must never disrupt the share/copy the organizer just performed.
  const recordActivationSignal = async (inviteId, signal) => {
    if (isOrganizerDraft) return;
    try {
      const payload = await apiRequest(
        `/events/${event.id}/invites/${inviteId}/activation-signal`,
        { method: "POST", token, data: { signal } }
      );
      onUpdate?.(payload);
    } catch { /* activation signal is best-effort */ }
  };

  const [sending, setSending] = useState(false);

  // Email each prepared invitation its private RSVP link via the gated first-send
  // delivery path. Returns only content-free counts; fails closed to a clear
  // message when delivery isn't enabled/configured on this deployment.
  const handleSendByEmail = async () => {
    setSending(true);
    try {
      const report = await apiRequest(`/events/${event.id}/invites/send`, {
        method: "POST",
        token,
        data: {},
      });
      if (report.status === "unavailable") {
        toast.error(
          report.error_code === "delivery_disabled"
            ? "Email delivery isn't turned on for this deployment yet."
            : report.error_code === "provider_unconfigured"
              ? "Email delivery isn't fully configured yet."
              : "Email delivery is unavailable right now."
        );
      } else {
        const counts = report.counts || {};
        const sent = counts.submitted || 0;
        toast.success(
          `Emailed ${sent} invitation${sent === 1 ? "" : "s"}.` +
            (counts.skipped ? ` ${counts.skipped} already sent.` : "")
        );
        try {
          const fresh = await apiRequest(`/events/${event.id}`, { token });
          onUpdate?.(fresh);
        } catch {
          /* refreshing the activation funnel is best-effort */
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to send invitations.");
    } finally {
      setSending(false);
    }
  };

  const toggleMember = (memberId) => {
    setDraftPreviewed(false);
    setForm((c) => ({
      ...c,
      member_ids: c.member_ids.includes(memberId)
        ? c.member_ids.filter((id) => id !== memberId)
        : [...c.member_ids, memberId],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isOrganizerDraft) {
      setDraftPreviewed(true);
      return;
    }
    setSending(true);
    try {
      const payload = await apiRequest(`/events/${event.id}/invites`, {
        method: "POST",
        token,
        data: {
          member_ids: form.member_ids,
          guest_emails: form.guest_emails.split(",").map((s) => s.trim()).filter(Boolean),
          note: form.note,
        },
      });
      const createdCount = Math.max(
        0,
        (payload.event_invites?.length || 0) - (event.event_invites?.length || 0)
      );
      if (event.event_template === "reunion" && createdCount > 0) {
        trackReunionEvent("invite_created", {
          source: "gathering_invites",
          invite_count: createdCount,
        });
        if ((event.event_invites?.length || 0) === 0) {
          trackReunionEvent("first_invitation_prepared", { source: "gathering_invites" });
        }
      }
      onUpdate(payload);
      setForm(initialInviteForm);
      // One step: email the invitations right after creating them, so the
      // organizer isn't left hunting for a separate send button.
      let sentMessage = "Invitations created.";
      try {
        const report = await apiRequest(`/events/${event.id}/invites/send`, {
          method: "POST",
          token,
          data: {},
        });
        if (report.status === "unavailable") {
          sentMessage =
            "Invitations created — email delivery is off, so share the links below.";
        } else {
          const sent = report.counts?.submitted || 0;
          sentMessage = `Invited and emailed ${sent} ${sent === 1 ? "person" : "people"} ✓`;
        }
        const fresh = await apiRequest(`/events/${event.id}`, { token });
        onUpdate?.(fresh);
      } catch {
        sentMessage = "Invitations created. You can email them with the button below.";
      }
      toast.success(sentMessage);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to create invites.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="soft-panel" data-testid="gatherings-invites-panel">
      <div className="flex items-center gap-3">
        <MailPlus className="h-4 w-4 text-primary" />
        <p className="text-lg font-semibold text-foreground">Invite people to this gathering</p>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {members.map((member) => (
          <button
            className={`rounded-full px-3 py-2 text-xs font-semibold transition ${form.member_ids.includes(member.id) ? "bg-primary text-primary-foreground" : "border border-border bg-background/80 text-foreground"}`}
            data-testid={`gatherings-invite-member-${member.id}`}
            key={member.id}
            onClick={() => toggleMember(member.id)}
            type="button"
          >
            {member.full_name}
          </button>
        ))}
      </div>
      <form className="mt-4 space-y-3" onSubmit={handleSubmit}>
        <Input className="field-input" data-testid="gatherings-invite-guests-input" onChange={(e) => { setDraftPreviewed(false); setForm((c) => ({ ...c, guest_emails: e.target.value })); }} placeholder="guest1@example.com, guest2@example.com" value={form.guest_emails} />
        <Textarea className="field-textarea" data-testid="gatherings-invite-note-input" onChange={(e) => { setDraftPreviewed(false); setForm((c) => ({ ...c, note: e.target.value })); }} placeholder="Optional note for invitees" value={form.note} />
        {isOrganizerDraft && draftPreviewed ? (
          <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4" data-testid="holiday-invitation-plan-preview">
            <p className="text-sm font-semibold text-foreground">Private invitation plan preview</p>
            <p className="mt-2 text-xs leading-5 text-muted-foreground">
              {form.member_ids.length} family member {form.member_ids.length === 1 ? "invitation" : "invitations"} and {guestCount} guest {guestCount === 1 ? "invitation" : "invitations"}. Optional note: {form.note.trim() ? "included" : "not included"}.
            </p>
            <p className="mt-2 text-xs leading-5 text-muted-foreground">No links, credentials, notifications, or provider requests were created.</p>
          </div>
        ) : null}
        <Button className="w-full rounded-full" data-testid="gatherings-invite-submit-button" disabled={sending} type="submit" variant="secondary">
          {isOrganizerDraft
            ? "Preview invitation plan"
            : sending
              ? "Sending…"
              : "Create & send invitations"}
        </Button>
        {isOrganizerDraft ? (
          <p className="text-xs leading-5 text-muted-foreground" data-testid="holiday-invitation-draft-guard">
            Finish the private organizer checklist before Kindred can create invitation credentials.
          </p>
        ) : null}
      </form>
      <div className="mt-4 space-y-3">
        {event.event_invites?.map((invite, index) => (
          <div className="rounded-2xl border border-border/70 bg-background/70 px-4 py-3" data-testid={`gatherings-event-invite-${invite.id}`} key={invite.id}>
            <p className="text-sm font-semibold text-foreground">{invite.invitee_name} · {invite.email}</p>
            <p className="mt-1 text-xs uppercase tracking-[0.14em] text-muted-foreground">{invite.invite_source} · {invite.delivery_status}</p>
            {invite.note ? <p className="mt-2 text-sm text-muted-foreground">{invite.note}</p> : null}
            {invite.zoom_link ? <p className="mt-2 text-sm text-primary" data-testid={`gatherings-event-invite-zoom-${invite.id}`}>Zoom: {invite.zoom_link}</p> : null}
            {invite.share_message && (
              <div className="mt-2 flex items-start gap-2">
                <p className="flex-1 text-sm leading-7 text-muted-foreground" data-testid={`gatherings-event-invite-message-${invite.id}`}>{invite.share_message}</p>
                <ShareMessageButton
                  isReunion={event.event_template === "reunion"}
                  isFirst={index === 0}
                  onSignal={(signal) => recordActivationSignal(invite.id, signal)}
                  text={invite.share_message}
                  testId={`gatherings-event-invite-share-${invite.id}`}
                />
              </div>
            )}
            <CopyRsvpLinkButton
              inviteId={invite.id}
              isFirst={index === 0}
              isReunion={event.event_template === "reunion"}
              onSignal={(signal) => recordActivationSignal(invite.id, signal)}
            />
          </div>
        ))}
      </div>
      {!isOrganizerDraft && event.event_invites?.length > 0 ? (
        <div className="mt-4">
          <Button
            className="w-full rounded-full"
            data-testid="gatherings-invite-send-email"
            disabled={sending}
            onClick={handleSendByEmail}
            type="button"
          >
            {sending ? "Sending…" : "Email pending invitations"}
          </Button>
          <p className="mt-2 text-xs leading-5 text-muted-foreground">
            Re-sends the private RSVP link to anyone not yet emailed (already
            delivered invitations are skipped). Requires email delivery to be enabled.
          </p>
        </div>
      ) : null}
    </div>
  );
};
